/** postMessage bridge to the loader (AGENTS.md 5.2). Strict origin: the loader's page origin is learned from the init message. */
import {
  PREVIEW_PARENT_ORIGINS,
  PREVIEW_SOURCE,
  WIDGET_PROTOCOL_SOURCE,
  isPreviewMessage,
  isWidgetMessage,
  type InitPayload,
  type PreviewPayload,
  type WidgetMessage,
} from "@wapadesk/schema/protocol";

let hostOrigin: string | undefined;
let initPayload: InitPayload | undefined;
const listeners = new Set<(m: WidgetMessage) => void>();

const STORAGE_PREFIX = "wapadesk.session.";

function standaloneInit(): InitPayload | undefined {
  const parts = location.pathname.split("/");
  const kind = parts[1];
  const id = decodeURIComponent(parts[2] ?? "");
  if (!id) return undefined;
  const q = new URLSearchParams(location.search);
  const utm: Record<string, string> = {};
  for (const [k, v] of q) if (k.startsWith("utm_")) utm[k] = v;
  let sessionToken: string | undefined;
  try {
    sessionToken = localStorage.getItem(STORAGE_PREFIX + id) ?? undefined;
  } catch {
    /* storage blocked: the conversation still works for this visit */
  }
  return {
    clinicId: kind === "go" ? "" : id,
    ...(kind === "go" ? { slug: id } : {}),
    standalone: true,
    ...(sessionToken ? { sessionToken } : {}),
    context: {
      pageUrl: `${location.origin}${location.pathname}${location.search}`,
      ...(document.referrer ? { referrer: document.referrer } : {}),
      ...(Object.keys(utm).length ? { utm } : {}),
      language: navigator.language,
      device: window.matchMedia?.("(max-width: 640px)").matches ? "mobile" : "desktop",
    },
  };
}

/** Messages that would go to the loader are handled here when there is no loader (share link). */
function handleLocally(msg: Exclude<WidgetMessage, { type: "init" }>): void {
  const id = initPayload?.slug ?? initPayload?.clinicId;
  if (msg.type === "session" && id) {
    try {
      if (msg.payload.sessionToken)
        localStorage.setItem(STORAGE_PREFIX + id, msg.payload.sessionToken);
      else localStorage.removeItem(STORAGE_PREFIX + id);
    } catch {
      /* ignore */
    }
  } else if (msg.type === "navigate") {
    if (msg.payload.target === "_blank") window.open(msg.payload.url, "_blank", "noopener");
    else location.assign(msg.payload.url);
  }
  // close, resize, state and track have no meaning without a host page.
}

let previewing = false;
/** The draft the dashboard sent for the live preview (only on /preview). */
export let previewPayload: PreviewPayload | undefined;

/** Live funnel preview: the dashboard sends the draft; nothing reaches a host page. */
function startPreview(onInit: (p: InitPayload) => void): () => void {
  previewing = true;
  const handler = (ev: MessageEvent) => {
    if (!(PREVIEW_PARENT_ORIGINS as readonly string[]).includes(ev.origin)) return;
    if (!isPreviewMessage(ev.data) || ev.data.type !== "preview") return;
    previewPayload = ev.data.payload;
    const cfg = previewPayload.config as { clinicId?: string };
    onInit({
      clinicId: cfg.clinicId ?? "preview",
      standalone: true,
      context: {
        pageUrl: "https://your-website.example/page",
        language: navigator.language,
        device: window.innerWidth >= 1024 ? "desktop" : "mobile",
      },
    });
  };
  window.addEventListener("message", handler);
  window.parent.postMessage({ source: PREVIEW_SOURCE, type: "ready", payload: {} }, "*");
  return () => window.removeEventListener("message", handler);
}

export function startBridge(onInit: (p: InitPayload) => void, timeoutMs = 1500): () => void {
  if (location.pathname.replace(/\/+$/, "") === "/preview" && window.parent !== window)
    return startPreview(onInit);
  // A share link (/go/<slug>) runs standalone: at the top level, or inside an iframe that asks for
  // it with ?embed=1 (wapadesk.com shows the demo clinic's chat and booking flow that way).
  const embedded = new URLSearchParams(location.search).get("embed") === "1";
  if ((window.parent === window || embedded) && location.pathname.startsWith("/go/")) {
    const p = standaloneInit();
    if (p) {
      initPayload = p;
      onInit(p);
      return () => undefined;
    }
  }
  const handler = (ev: MessageEvent) => {
    if (!isWidgetMessage(ev.data)) return;
    if (ev.data.type === "init") {
      if (hostOrigin && ev.origin !== hostOrigin) return;
      hostOrigin = ev.origin;
      initPayload = ev.data.payload;
      onInit(ev.data.payload);
      return;
    }
    if (hostOrigin && ev.origin !== hostOrigin) return;
    for (const l of listeners) l(ev.data);
  };
  window.addEventListener("message", handler);
  // Announce readiness; the loader answers with init.
  if (window.parent !== window)
    window.parent.postMessage({ source: WIDGET_PROTOCOL_SOURCE, type: "ready", payload: {} }, "*");
  const timer = setTimeout(() => {
    // Opened directly (no loader): run standalone with the clinic id from the URL.
    if (!initPayload) {
      const p = standaloneInit();
      if (p) {
        initPayload = p;
        onInit(p);
      }
    }
  }, timeoutMs);
  return () => {
    clearTimeout(timer);
    window.removeEventListener("message", handler);
  };
}

export function send(msg: Exclude<WidgetMessage, { type: "init" }>): void {
  if (previewing) return; // the dashboard is not a host page
  if (window.parent === window) {
    handleLocally(msg);
    return;
  }
  window.parent.postMessage(msg, hostOrigin ?? "*");
}

export const isEmbedded = () => window.parent !== window;
